import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Clock3,
  CreditCard,
  Loader2,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Search,
  WalletCards,
} from 'lucide-react';
import paymentApi from '../../utils/paymentApi';
import { getPaymentErrorCode, paymentErrorMessage, paymentResponseError } from './paymentErrors';
import { getBillingHistory, getCreditAccountLedger } from './billingHistoryApi';
import { formatBillingDate, getBillingLogLabel } from './billingData';
import { getCreditLedgerActivity } from './creditLedgerTypes';
import { creditOperationStatus, isCreditOperationActive } from './CreditLedgerDetails';
import CreditBalanceAfter from './CreditBalanceAfter';
import PaymentCancellationNotice from './PaymentCancellationNotice';
import { cancellationPreviewData } from './billingCancellationPreview';
import {
  opensCashierWindow,
  paymentMethodFromChannel,
  paymentMethodLabel,
  paymentPrefix,
} from './paymentChannels';

const ORDER_FILTERS = [
  { value: '', label: '全部', labelEn: 'All' },
  { value: 'pending', label: '待支付', labelEn: 'Pending' },
  { value: 'paid', label: '已完成', labelEn: 'Completed' },
  { value: 'refunding', label: '退款中', labelEn: 'Refunding' },
  { value: 'refunded', label: '已退款', labelEn: 'Refunded' },
];

const WALLET_FILTERS = [
  { value: 'all', label: '全部变动', labelEn: 'All activity' },
  { value: 'income', label: '点数增加', labelEn: 'Credits added' },
  { value: 'expense', label: '点数消耗', labelEn: 'Credits used' },
];

const WALLET_PAGE_SIZE = 10;

function statusTone(status) {
  if (status === 'paid' || status === 'success') return 'success';
  if (status === 'refunding') return 'warning';
  if (status === 'refunded' || status === 'cancelled' || status === 'failed' || status === 'closed') return 'quiet';
  return 'pending';
}

function paymentDate(order) {
  return order.paid_at || order.created_at;
}

const REFUND_STATUS_META = {
  prepared: {
    label: '退款已准备',
    labelEn: 'Refund prepared',
    tone: 'pending',
    detail: '退款事务已创建，系统将继续提交支付渠道，请勿重复操作。',
    detailEn: 'The refund record is ready and will be submitted automatically. Do not create another refund.',
    timeLabel: '最近更新',
    timeLabelEn: 'Updated',
  },
  submitting: {
    label: '正在提交退款',
    labelEn: 'Submitting refund',
    tone: 'warning',
    detail: '退款正在提交支付渠道，请等待系统确认受理结果。',
    detailEn: 'The refund is being submitted to the payment provider. Wait for the acceptance result.',
    timeLabel: '最近更新',
    timeLabelEn: 'Updated',
  },
  processing: {
    label: '退款处理中',
    labelEn: 'Refund processing',
    tone: 'warning',
    detail: '管理员已提交原路退款，到账时间以支付渠道为准。',
    detailEn: 'An administrator submitted the refund to the original payment method. Arrival time depends on the payment provider.',
    timeLabel: '最近更新',
    timeLabelEn: 'Updated',
  },
  unknown: {
    label: '退款状态确认中',
    labelEn: 'Refund status pending',
    tone: 'pending',
    detail: '支付渠道暂未返回明确结果，管理员会继续查单确认，请勿重复操作。',
    detailEn: 'The payment provider has not returned a final result. An administrator will continue checking it.',
    timeLabel: '最近更新',
    timeLabelEn: 'Updated',
  },
  succeeded: {
    label: '已原路退款',
    labelEn: 'Refunded to original method',
    tone: 'success',
    detail: '退款已由支付渠道确认，实际到账时间以银行或支付机构为准。',
    detailEn: 'The payment provider confirmed the refund. Actual arrival time depends on the bank or provider.',
    timeLabel: '退款完成',
    timeLabelEn: 'Completed',
  },
  failed: {
    label: '退款失败',
    labelEn: '退款失败',
    tone: 'danger',
    detail: '本次原路退款未成功，冻结点数已按原有效期处理；同一订单不能再次申请，请联系管理员核对。',
    detailEn: '本次原路退款未成功，冻结点数已按原有效期处理；同一订单不能再次申请，请联系管理员核对。',
    timeLabel: '处理时间',
    timeLabelEn: 'Processed',
  },
  compensation_pending: {
    label: '退款待人工核对',
    labelEn: '退款待人工核对',
    tone: 'danger',
    detail: '资金与点数权益状态需要管理员人工核对，处理完成前请勿重复操作。',
    detailEn: '资金与点数权益状态需要管理员人工核对，处理完成前请勿重复操作。',
    timeLabel: '最近更新',
    timeLabelEn: 'Updated',
  },
};

function refundMeta(refund) {
  return REFUND_STATUS_META[String(refund?.status || '').toLowerCase()] || REFUND_STATUS_META.unknown;
}

function refundTime(refund) {
  if (!refund) return null;
  if (String(refund.status || '').toLowerCase() === 'succeeded') return refund.succeeded_at || refund.updated_at || refund.requested_at;
  return refund.updated_at || refund.requested_at;
}

function refundAmount(refund) {
  const amount = Number(refund?.amount);
  return Number.isFinite(amount) ? amount.toFixed(2) : null;
}

export default function BillingOrdersPanel({ preview = false, english = false }) {
  const cancellationPreview = preview && new URLSearchParams(window.location.search).get('scenario') === 'alipay-cancellation';
  const [previewCancellationState, setPreviewCancellationState] = useState('pending');
  const [cancellationFeedback, setCancellationFeedback] = useState({});
  const [filter, setFilter] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ list: [], total: 0, page: 1, per_page: 10, summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [walletData, setWalletData] = useState({ list: [], total: 0, per_page: WALLET_PAGE_SIZE });
  const walletRequest = useRef(0);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState('');
  const [walletFilter, setWalletFilter] = useState('all');
  const [walletPage, setWalletPage] = useState(1);
  const [scope, setScope] = useState('personal');
  const walletParams = useRef(null);
  walletParams.current = { page: walletPage, per_page: WALLET_PAGE_SIZE, direction: walletFilter, scope };
  const [checkingPayNo, setCheckingPayNo] = useState('');
  const [resumingPayNo, setResumingPayNo] = useState('');
  const [cancellingPayNo, setCancellingPayNo] = useState('');
  const [resumedWechat, setResumedWechat] = useState(null);
  const [paymentNotice, setPaymentNotice] = useState({ type: '', message: '' });
  const [statusError, setStatusError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (preview) {
        if (cancellationPreview) {
          const demo = cancellationPreviewData(previewCancellationState);
          const matches = scope === 'personal' && (!filter || filter === previewCancellationState)
            && (!keyword || demo.list[0].order_no.includes(keyword));
          setData({ ...demo, list: matches ? demo.list : [], total: matches ? 1 : 0 });
          return;
        }
        setData({
          list: [], total: 0, page: 1, per_page: 10,
          summary: { total_orders: 0, paid_orders: 0, refund_orders: 0, paid_amount: '0.00' },
        });
        return;
      }
      setData(await getBillingHistory({ page, per_page: 10, status: filter || undefined, keyword: keyword || undefined, scope }));
    } catch (requestError) {
      setError(getErrorMessage(requestError, '账单记录暂时无法加载'));
    } finally {
      setLoading(false);
    }
  }, [filter, keyword, page, preview, scope, cancellationPreview, previewCancellationState]);

  useEffect(() => { load(); }, [load]);

  const loadWallet = useCallback(async (quiet = false) => {
    // Payment completion callbacks also refresh the currently selected wallet.
    const params = walletParams.current;
    const requestId = ++walletRequest.current;
    if (quiet !== true) setWalletLoading(true);
    setWalletError('');
    try {
      const result = preview
        ? { list: [], total: 0, page: 1, per_page: WALLET_PAGE_SIZE, available_credits: 0 }
        : await getCreditAccountLedger(params);
      if (requestId === walletRequest.current) {
        setWalletData(result);
        if (result.page && result.page !== params.page) setWalletPage(result.page);
      }
    } catch (requestError) {
      if (requestId === walletRequest.current) setWalletError(getErrorMessage(requestError, '点数明细暂时无法加载'));
    } finally {
      if (requestId === walletRequest.current) setWalletLoading(false);
    }
  }, [preview]);

  useEffect(() => {
    loadWallet();
    return () => { walletRequest.current += 1; };
  }, [loadWallet, scope, walletFilter, walletPage]);

  const totalPages = Math.max(1, Math.ceil(Number(data.total || 0) / Number(data.per_page || 10)));
  const summary = data.summary || {};
  const rows = useMemo(() => (Array.isArray(data.list) ? data.list : []), [data.list]);
  const walletRows = Array.isArray(walletData?.list) ? walletData.list : [];
  const walletPageCount = Math.max(1, Math.ceil(Number(walletData?.total || 0) / Number(walletData?.per_page || WALLET_PAGE_SIZE)));
  const hasActiveOperation = walletRows.some(isCreditOperationActive);
  useEffect(() => {
    if (preview || !hasActiveOperation) return undefined;
    const timer = window.setInterval(() => { if (!document.hidden) loadWallet(true); }, 10000);
    return () => window.clearInterval(timer);
  }, [preview, hasActiveOperation, loadWallet]);

  const search = event => {
    event.preventDefault();
    setPage(1);
    setKeyword(keywordInput.trim());
  };

  const checkPendingPayment = async payment => {
    setCheckingPayNo(payment.pay_no);
    setPaymentNotice({ type: '', message: '' });
    try {
      const method = paymentMethodFromChannel(payment.channel);
      if (!method) throw new Error('该支付渠道暂不支持查单');
      const endpoint = `${paymentPrefix(method)}/query`;
      const response = await paymentApi.get(endpoint, { params: { pay_no: payment.pay_no } });
      if (Number(response?.data?.code) !== 200) {
        throw paymentResponseError(response?.data);
      }
      const result = response.data.data || {};
      const settled = Boolean(result.settled) || result.status === 'success';
      const closed = result.status === 'closed' || result.status === 'failed';
      setPaymentNotice({
        type: settled ? 'success' : (closed ? 'warning' : 'pending'),
        message: settled
          ? (english ? 'Payment confirmed and credited.' : '支付已确认，点数或订阅已到账')
          : (closed
            ? (english ? 'This payment attempt is closed. You can continue payment on the order.' : '本次支付单已关闭，可在该订单上继续支付')
            : (english ? 'The channel has not confirmed payment yet. Do not pay twice.' : '渠道尚未确认付款，请勿重复支付')),
      });
      await Promise.all([load(), loadWallet()]);
    } catch (checkError) {
      setPaymentNotice({
        type: 'error',
        message: paymentErrorMessage(checkError, english, 'query'),
      });
    } finally {
      setCheckingPayNo('');
    }
  };

  const submitPaymentCancellation = async payment => {
    const method = paymentMethodFromChannel(payment.channel);
    const response = await paymentApi.post(`${paymentPrefix(method)}/cancel`, { pay_no: payment.pay_no });
    const body = response?.data;
    if (Number(body?.code) !== 200) {
      throw paymentResponseError(body);
    }

    return body.data || {};
  };

  const finishPaymentCancellation = async (result, { expired = false, method = 'alipay' } = {}) => {
    if (result.settled || result.status === 'success') {
      setPaymentNotice({
        type: 'success',
        message: english ? 'Payment confirmed and credited.' : '支付已确认，点数或订阅已到账',
      });
      await Promise.all([load(), loadWallet()]);
      return;
    }
    if (result.cancellation_pending === true) {
      const channelLabel = paymentMethodLabel(method, english);
      setPaymentNotice({
        type: 'warning',
        message: english
          ? `The order closure is being confirmed. Do not use the old ${channelLabel} payment link or QR code.`
          : (expired
            ? `订单已过期，系统正在向${channelLabel}确认关单，请勿再使用原付款入口`
            : `取消请求已提交，请勿再使用原付款入口，系统正在等待${channelLabel}确认`),
      });
      await load();
      return;
    }
    if (!['closed', 'failed'].includes(String(result.status || ''))) {
      throw new Error(english ? 'The payment state is still being verified. Try again later.' : `${paymentMethodLabel(method)}状态仍在确认，请稍后重试`);
    }

    setPaymentNotice({
      type: 'success',
      message: english
        ? 'Order closed. Place a new order if you still want to purchase.'
        : (expired ? '订单已超过30分钟并自动关闭，请重新下单' : '订单已取消，如需购买请重新下单'),
    });
    await load();
  };

  const refreshCancellation = async payment => {
    if (checkingPayNo === payment.pay_no) return;
    setCheckingPayNo(payment.pay_no);
    setPaymentNotice({ type: '', message: '' });
    setCancellationFeedback(current => ({ ...current, [payment.pay_no]: null }));
    try {
      if (cancellationPreview) {
        await new Promise(resolve => window.setTimeout(resolve, 600));
        setPreviewCancellationState('cancelled');
        setPaymentNotice({ type: 'success', message: english ? 'Demo: cancellation confirmed.' : '演示：支付宝已确认关单，订单已取消。' });
        return;
      }
      // Reconcile the saved cancellation intent, including closing a still-unpaid trade.
      const result = await submitPaymentCancellation(payment);
      if (result.cancellation_pending && !result.settled && result.status !== 'success') {
        setCancellationFeedback(current => ({ ...current, [payment.pay_no]: {
          type: 'pending', message: english ? 'Checked just now. Closure is not confirmed yet; please check again later.' : '刚刚已核对，支付宝尚未确认关单，请稍后再次刷新。',
        } }));
        await load();
      } else {
        await finishPaymentCancellation(result);
      }
    } catch (refreshError) {
      setCancellationFeedback(current => ({ ...current, [payment.pay_no]: {
        type: 'error', message: '本次核对未完成，取消申请仍保留，请稍后重试。',
      } }));
    } finally {
      setCheckingPayNo('');
    }
  };

  const continuePendingPayment = async (order, payment) => {
    const channel = String(payment.channel || '');
    const method = paymentMethodFromChannel(channel);
    const isWechat = method === 'wechat';
    const isAlipay = method === 'alipay';
    if (!method) {
      setPaymentNotice({
        type: 'error',
        message: '该支付渠道暂不支持继续支付',
      });
      return;
    }

    const payWindow = opensCashierWindow(method) ? window.open('', '_blank') : null;
    if (opensCashierWindow(method) && !payWindow) {
      setPaymentNotice({
        type: 'error',
        message: getErrorMessage(english ? 'Please allow pop-ups and try again.' : '支付窗口被浏览器拦截，请允许弹窗后重试', '支付窗口被浏览器拦截，请允许弹窗后重试'),
      });
      return;
    }

    setResumingPayNo(payment.pay_no);
    setPaymentNotice({ type: '', message: '' });
    try {
      const endpoint = `${paymentPrefix(method)}/pay`;
      const response = await paymentApi.post(endpoint, {
        order_id: order.id,
        ...(isAlipay ? { scene: channel === 'alipay_wap' ? 'wap' : 'page' } : {}),
      });
      const body = response?.data;
      if (Number(body?.code) !== 200) {
        throw paymentResponseError(body);
      }
      if (body.data?.status === 'success') {
        payWindow?.close();
        setResumedWechat(null);
        setPaymentNotice({
          type: 'success',
          message: english ? 'Payment confirmed and credited.' : '支付已确认，点数或订阅已到账',
        });
        await Promise.all([load(), loadWallet()]);
        return;
      }
      if (body.data?.expired_closed === true || ['closed', 'failed'].includes(String(body.data?.status || ''))) {
        payWindow?.close();
        setPaymentNotice({
          type: 'success',
          message: english ? 'Order closed. Place a new order to continue.' : '订单已超过30分钟并自动关闭，请重新下单',
        });
        await load();
        return;
      }
      if (!body.data?.pay_no) {
        throw new Error(english ? 'The payment number is missing.' : '支付单号缺失，请稍后重试');
      }

      if (isWechat) {
        if (!body.data?.code_url) {
          throw new Error('微信支付二维码获取失败');
        }
        setResumedWechat({
          orderId: order.id,
          payNo: body.data.pay_no,
          codeUrl: body.data.code_url,
          expiresAt: body.data?.expires_at || null,
        });
        setPaymentNotice({
          type: 'pending',
          message: english ? 'A new WeChat QR code is ready. Scan it to pay.' : '新的微信支付二维码已生成，请扫码完成支付',
        });
      } else {
        if (!body.data?.form) {
          throw new Error(english ? 'Missing cashier form.' : '支付收银台信息缺失');
        }
        payWindow.document.write(body.data.form);
        payWindow.document.close();
        setPaymentNotice({
          type: 'pending',
          message: english
            ? `${paymentMethodLabel(method, true)} cashier reopened in a new window.`
            : `已重新打开${paymentMethodLabel(method)}收银台`,
        });
      }
      await load();
    } catch (resumeError) {
      payWindow?.close();
      const errorCode = getPaymentErrorCode(resumeError);
      if (isAlipay && errorCode === 'ORDER_EXPIRED') {
        try {
          const cancellationResult = await submitPaymentCancellation(payment);
          await finishPaymentCancellation(cancellationResult, { expired: true });
        } catch (cancellationError) {
          setPaymentNotice({
            type: 'error',
            message: paymentErrorMessage(cancellationError, english, 'cancel'),
          });
        }
        return;
      }
      setPaymentNotice({
        type: 'error',
        message: paymentErrorMessage(resumeError, english),
      });
    } finally {
      setResumingPayNo('');
    }
  };

  const cancelPendingPayment = async (order, payment) => {
    const method = paymentMethodFromChannel(payment.channel);
    const paymentEntry = method === 'wechat'
      ? (english ? 'WeChat QR code' : '微信支付二维码')
      : (english ? 'Alipay cashier' : '支付宝收银台');
    const confirmed = window.confirm(english
      ? `Cancel order ${order.order_no}? Do not use the old ${paymentEntry} after submitting. A payment already completed will still be credited.`
      : `确认取消订单 ${order.order_no}？提交后请勿继续使用原${paymentEntry}；如已完成付款，系统仍会按实际支付结果入账。`);
    if (!confirmed) return;

    setCancellingPayNo(payment.pay_no);
    setPaymentNotice({ type: '', message: '' });
    try {
      const result = await submitPaymentCancellation(payment);
      await finishPaymentCancellation(result, { method });
      setResumedWechat(current => current?.orderId === order.id ? null : current);
    } catch (cancelError) {
      setPaymentNotice({
        type: 'error',
        message: paymentErrorMessage(cancelError, english, 'cancel'),
      });
    } finally {
      setCancellingPayNo('');
    }
  };

  useEffect(() => {
    setStatusError('');
    if (!resumedWechat?.payNo) return undefined;

    let stopped = false;
    let expiryTimer = null;
    if (resumedWechat.expiresAt) {
      const expiresAtMs = new Date(resumedWechat.expiresAt).getTime();
      if (Number.isFinite(expiresAtMs)) {
        const delay = expiresAtMs - Date.now();
        if (delay <= 0) {
          setResumedWechat(null);
          setPaymentNotice({
            type: 'warning',
            message: '微信二维码已过期，请重新点击继续支付',
          });
          return undefined;
        }
        expiryTimer = window.setTimeout(() => {
          setResumedWechat(null);
          setPaymentNotice({
            type: 'warning',
            message: '微信二维码已过期，请重新点击继续支付',
          });
        }, delay);
      }
    }

    const pollTimer = window.setInterval(async () => {
      try {
        const response = await paymentApi.get('/wechat-pay/status', {
          params: { pay_no: resumedWechat.payNo },
        });
        if (Number(response?.data?.code) !== 200) throw paymentResponseError(response?.data);
        if (stopped) return;
        setStatusError('');
        if (!stopped && response.data?.data?.status === 'success') {
          stopped = true;
          window.clearInterval(pollTimer);
          if (expiryTimer) window.clearTimeout(expiryTimer);
          setResumedWechat(null);
          setPaymentNotice({
            type: 'success',
            message: english ? 'Payment confirmed and credited.' : '支付已确认，点数或订阅已到账',
          });
          await Promise.all([load(), loadWallet()]);
        }
      } catch (requestError) {
        if (!stopped) setStatusError(paymentErrorMessage(requestError, english, 'query'));
      }
    }, 3000);

    return () => {
      stopped = true;
      window.clearInterval(pollTimer);
      if (expiryTimer) window.clearTimeout(expiryTimer);
    };
  }, [english, load, loadWallet, resumedWechat]);

  return (
    <div className="account-content__stack">
      <section className="account-page-heading">
        <div>
          <span className="account-eyebrow">ORDERS & PAYMENTS</span>
          <h1>{english ? 'Billing History' : '账单记录'}</h1>
          <p>{english ? 'Review orders, payment records, refunds, and credit delivery.' : '查看订单、支付记录、退款状态和点数到账。'}</p>
        </div>
        <button className="billing-history-refresh" type="button" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'is-spinning' : ''} />
          {english ? 'Refresh' : '刷新'}
        </button>
      </section>

      {cancellationPreview && <div className="billing-cancellation-demo">
        <span>{english ? 'Cancellation flow demo · sample data' : '取消流程演示 · 以下为示例数据'}</span>
        <div className="billing-segmented" role="group" aria-label={english ? 'Demo state' : '演示状态'}>
          {['pending', 'cancelled', 'paid'].map(value => <button type="button" key={value} aria-pressed={previewCancellationState === value} className={previewCancellationState === value ? 'is-active' : ''} disabled={Boolean(checkingPayNo)} onClick={() => { setPreviewCancellationState(value); setPaymentNotice({ type: '', message: '' }); setExpanded({}); }}>
            {english ? ({ pending: 'Awaiting confirmation', cancelled: 'Cancelled', paid: 'Paid' }[value]) : ({ pending: '待确认', cancelled: '已取消', paid: '已付款' }[value])}
          </button>)}
        </div>
      </div>}

      <div className="billing-order-summary-grid">
        <article><span><ReceiptText size={16} /></span><div><strong>{english ? 'All orders' : '全部订单'}</strong><small>{english ? 'All order states' : '包含全部状态'}</small></div><b>{Number(summary.total_orders || 0)}</b></article>
        <article><span><WalletCards size={16} /></span><div><strong>{english ? 'Paid amount' : '累计实付'}</strong><small>{Number(summary.paid_orders || 0)} {english ? 'completed' : '笔已完成'}</small></div><b className="is-money">¥{Number(summary.paid_amount || 0).toFixed(2)}</b></article>
        <article><span><RotateCcw size={16} /></span><div><strong>{english ? 'Refund records' : '退款记录'}</strong><small>{english ? 'Refunding and refunded' : '退款中与已退款'}</small></div><b>{Number(summary.refund_orders || 0)}</b></article>
      </div>

      <section className="billing-orders billing-orders--connected">
        <header><div><span className="account-eyebrow">ORDER HISTORY</span><h2>{english ? 'Order history' : '订单明细'}</h2></div></header>
        <div className="billing-orders__toolbar">
          <div className="billing-segmented" role="radiogroup" aria-label={english ? 'Billing account' : '账单账户'}>
            <button type="button" role="radio" aria-checked={scope === 'personal'} className={scope === 'personal' ? 'is-active' : ''} onClick={() => { setScope('personal'); setPage(1); setWalletPage(1); }}>{english ? 'Personal' : '个人'}</button>
            <button type="button" role="radio" aria-checked={scope === 'team'} className={scope === 'team' ? 'is-active' : ''} onClick={() => { setScope('team'); setPage(1); setWalletPage(1); }}>{english ? 'Current team' : '当前团队'}</button>
          </div>
          <div className="billing-segmented" role="radiogroup" aria-label={english ? 'Order status' : '订单状态'}>
            {ORDER_FILTERS.map(item => (
              <button key={item.value || 'all'} type="button" role="radio" aria-checked={filter === item.value} className={filter === item.value ? 'is-active' : ''} onClick={() => { setFilter(item.value); setPage(1); }}>
                {english ? item.labelEn : item.label}
              </button>
            ))}
          </div>
          <form className="billing-order-search" onSubmit={search} role="search">
            <Search size={13} />
            <input aria-label={english ? 'Search order number' : '搜索订单号'} value={keywordInput} onChange={event => setKeywordInput(event.target.value)} placeholder={english ? 'Order / payment no.' : '订单号 / 支付单号'} maxLength={64} />
            <button type="submit">{english ? 'Search' : '查询'}</button>
          </form>
        </div>
        {paymentNotice.message && <p className={`billing-payment-notice is-${paymentNotice.type}`} role={paymentNotice.type === 'error' ? 'alert' : 'status'}>{paymentNotice.message}</p>}
        {statusError && <p className="billing-payment-notice is-error" role="alert">{statusError}</p>}

        {loading ? (
          <div className="billing-orders__state" aria-live="polite"><Loader2 size={20} className="is-spinning" /><strong>{english ? 'Loading billing history…' : '正在读取账单记录…'}</strong></div>
        ) : error ? (
          <div className="billing-orders__state is-error" role="alert"><CircleDashed size={22} /><strong>{error}</strong><button type="button" onClick={load}>{english ? 'Try again' : '重新加载'}</button></div>
        ) : rows.length === 0 ? (
          <div className="billing-orders__empty"><span><ReceiptText size={23} /></span><strong>{keyword || filter ? (english ? 'No matching orders' : '没有符合条件的订单') : (english ? 'No orders yet' : '暂无账单记录')}</strong><p>{english ? 'Orders will appear here after you create a credit payment.' : '创建点数支付订单后，会在这里保留完整记录。'}</p><NavLink to={preview ? '/prototype/vnext/billing/store' : '/billing/store'}>{english ? 'View credit packs' : '查看点数套餐'} <ChevronRight size={14} /></NavLink></div>
        ) : (
          <div className="billing-order-rows">
            {rows.map(order => {
              const attempts = Array.isArray(order.payments) ? order.payments : [];
              const cancellationRequested = order.status === 'pending' && (order.cancellation_requested === true
                || attempts.some(payment => payment.status === 'pending' && payment.cancellation_pending === true));
              const cancellationPayment = cancellationRequested ? attempts.find(payment => payment.status === 'pending'
                && paymentMethodFromChannel(payment.channel) === 'alipay') : null;
              const refund = order.refund_summary && typeof order.refund_summary === 'object'
                ? order.refund_summary
                : null;
              const refundStatus = refundMeta(refund);
              const refundUpdatedAt = refundTime(refund);
              const formattedRefundAmount = refundAmount(refund);
              const resumablePayment = attempts.find(payment => (
                Boolean(paymentMethodFromChannel(payment.channel))
              ));
              const open = Boolean(expanded[order.id]);
              return (
                <article className="billing-order-row" key={order.id}>
                  <div className="billing-order-row__main">
                    <span className="billing-order-row__icon"><CreditCard size={15} /></span>
                    <div className="billing-order-row__subject"><strong>{order.subject || (english ? 'Credit order' : '点数订单')}</strong><button type="button" onClick={() => setExpanded(current => ({ ...current, [order.id]: !open }))} aria-expanded={open} disabled={!attempts.length}>{order.order_no}{order.status === 'pending' && attempts.length > 0 && !cancellationRequested ? (english ? ' · Continue payment' : ' · 继续支付') : ''}<ChevronDown size={12} /></button></div>
                    <div className="billing-order-row__meta"><span>{Number(order.credits || 0).toLocaleString()} {english ? 'credits' : '点'}</span><time><Clock3 size={11} />{formatBillingDate(paymentDate(order), { locale: english ? 'en-US' : 'zh-CN' })}</time></div>
                    <strong className="billing-order-row__amount">¥{Number(order.total_amount || 0).toFixed(2)}</strong>
                    <span className={`billing-order-status is-${statusTone(order.status)}`}>{cancellationRequested ? (english ? 'Cancel requested' : '取消待确认') : order.status_label}</span>
                  </div>
                  {cancellationPayment && <PaymentCancellationNotice
                    requestedAt={order.cancel_requested_at}
                    english={english}
                    busy={[checkingPayNo, cancellingPayNo, resumingPayNo].includes(cancellationPayment.pay_no)}
                    feedback={cancellationFeedback[cancellationPayment.pay_no]}
                    onRefresh={() => refreshCancellation(cancellationPayment)}
                  />}
                  {refund && (
                    <section
                      className={`billing-refund-summary is-${refundStatus.tone}`}
                      aria-label={`${english ? 'Refund record' : '退款记录'} ${refund.refund_no || order.order_no}`}
                    >
                      <span className="billing-refund-summary__icon" aria-hidden="true"><RotateCcw size={14} /></span>
                      <div className="billing-refund-summary__content">
                        <div className="billing-refund-summary__heading">
                          <strong>{english ? refundStatus.labelEn : (refund.status_label || refundStatus.label)}</strong>
                          <span>{formattedRefundAmount === null ? (english ? 'Amount pending' : '金额待确认') : `¥${formattedRefundAmount}`}</span>
                          {refund.refund_no && <code>{refund.refund_no}</code>}
                        </div>
                        <p>{refundStatus.tone === 'danger' ? getErrorMessage(refund.public_message, refundStatus.detail) : english ? refundStatus.detailEn : (refund.public_message || refundStatus.detail)}</p>
                      </div>
                      <time dateTime={refundUpdatedAt || undefined}>
                        {english ? refundStatus.timeLabelEn : refundStatus.timeLabel}
                        {' · '}
                        {formatBillingDate(refundUpdatedAt, { locale: english ? 'en-US' : 'zh-CN' })}
                      </time>
                    </section>
                  )}
                  {open && attempts.length > 0 && (
                    <div className="billing-payment-attempts">
                      {attempts.map(payment => {
                        const cancellationPending = payment.status === 'pending'
                          && paymentMethodFromChannel(payment.channel) === 'alipay'
                          && (payment.cancellation_pending === true || cancellationRequested);
                        const canResume = order.status === 'pending'
                          && !cancellationPending
                          && payment.id === resumablePayment?.id;
                        const canCancel = payment.can_cancel === true
                          && !cancellationPending
                          && order.status === 'pending'
                          && payment.status === 'pending'
                          && (payment.channel === 'wechat_native' || paymentMethodFromChannel(payment.channel) === 'alipay');
                        const busy = checkingPayNo === payment.pay_no
                          || resumingPayNo === payment.pay_no
                          || cancellingPayNo === payment.pay_no;
                        return (
                          <div key={payment.id}>
                            <span>{payment.channel_label}</span>
                            <code>{payment.pay_no}</code>
                            <time>{formatBillingDate(payment.paid_at || payment.created_at, { locale: english ? 'en-US' : 'zh-CN' })}</time>
                            <b className={`is-${statusTone(payment.status)}`}>{cancellationPending ? (english ? 'Awaiting closure' : '等待关单确认') : payment.status_label}</b>
                            {(payment.status === 'pending' || canResume) && (
                              <span className="billing-payment-attempt-actions">
                                {canCancel && (
                                  <button className="is-danger" type="button" disabled={busy} onClick={() => cancelPendingPayment(order, payment)}>
                                    {cancellingPayNo === payment.pay_no ? (english ? 'Cancelling…' : '正在取消…') : (english ? 'Cancel order' : '取消订单')}
                                  </button>
                                )}
                                {payment.status === 'pending' && !cancellationPending && (
                                  <button type="button" disabled={busy} onClick={() => checkPendingPayment(payment)}>
                                    {checkingPayNo === payment.pay_no ? (english ? 'Checking…' : '查询中…') : (english ? 'Verify' : '查支付结果')}
                                  </button>
                                )}
                                {canResume && (
                                  <button className="is-primary" type="button" disabled={busy} onClick={() => continuePendingPayment(order, payment)}>
                                    {resumingPayNo === payment.pay_no ? (english ? 'Opening…' : '正在唤起…') : (english ? 'Continue payment' : '继续支付')}
                                  </button>
                                )}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {resumedWechat?.orderId === order.id && (
                        <section className="billing-payment-resume" aria-live="polite">
                          <span><QRCodeSVG value={resumedWechat.codeUrl} size={112} level="M" /></span>
                          <div>
                            <strong>{english ? 'Scan with WeChat to pay' : '请使用微信扫码支付'}</strong>
                            <small>{english ? 'This QR code belongs to the same order.' : '新二维码仍对应当前业务订单，不会重复创建订单。'}</small>
                            {resumedWechat.expiresAt && <small>{english ? 'QR code valid until' : '二维码有效期至'} {new Date(resumedWechat.expiresAt).toLocaleTimeString(english ? 'en-US' : 'zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</small>}
                            <code>{resumedWechat.payNo}</code>
                            <button type="button" onClick={() => cancelPendingPayment(order, { pay_no: resumedWechat.payNo, channel: 'wechat_native' })} disabled={[cancellingPayNo, checkingPayNo, resumingPayNo].includes(resumedWechat.payNo)}>
                              {cancellingPayNo === resumedWechat.payNo ? (english ? 'Cancelling…' : '正在取消…') : (english ? 'Cancel payment' : '取消支付')}
                            </button>
                          </div>
                        </section>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
        {!loading && !error && data.total > 0 && <footer className="billing-order-pagination"><span>{english ? `${data.total} orders` : `共 ${data.total} 笔`}</span><div><button type="button" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>{english ? 'Previous' : '上一页'}</button><span>{page} / {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>{english ? 'Next' : '下一页'}</button></div></footer>}
      </section>

      <section className="billing-evidence">
        <header>
          <div>
            <span className="account-eyebrow">WALLET EVIDENCE</span>
            <h2>{english ? 'Historical Credit Entries' : '历史点数入账'}</h2>
          </div>
          <span>{english ? 'View all credit increases and usage entries.' : '查看全部点数增加与点数消耗记录'}</span>
        </header>

        <div className="billing-evidence__toolbar">
          <div className="billing-segmented" role="radiogroup" aria-label={english ? 'Credit activity type' : '点数变动类型'}>
            {WALLET_FILTERS.map(item => (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={walletFilter === item.value}
                className={walletFilter === item.value ? 'is-active' : ''}
                onClick={() => { setWalletFilter(item.value); setWalletPage(1); }}
              >
                {english ? item.labelEn : item.label}
              </button>
            ))}
          </div>
          <div className="billing-evidence__balance">
            <span>{english ? 'Available' : '当前可用'}</span>
            <strong>{walletLoading || walletError ? '—' : Number(walletData?.available_credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</strong>
            <button type="button" onClick={loadWallet} disabled={walletLoading} aria-label={english ? 'Refresh credit activity' : '刷新点数明细'}>
              <RefreshCw size={12} className={walletLoading ? 'is-spinning' : ''} />
            </button>
          </div>
        </div>

        {walletLoading ? (
          <div className="billing-evidence__state" aria-live="polite"><Loader2 size={19} className="is-spinning" /><strong>{english ? 'Loading credit activity…' : '正在加载点数流水…'}</strong></div>
        ) : walletError ? (
          <div className="billing-evidence__state is-error" role="alert"><CircleDashed size={21} /><strong>{walletError}</strong><button type="button" onClick={loadWallet}>{english ? 'Try again' : '重新加载'}</button></div>
        ) : walletRows.length === 0 ? (
          <div className="billing-evidence__state"><WalletCards size={21} /><strong>{english ? 'No matching credit activity' : '暂无符合条件的点数明细'}</strong></div>
        ) : (
          <div className="billing-evidence__rows">
            {walletRows.map(item => {
              const amount = Number(item.amount || 0);
              const income = amount > 0;
              const activity = getCreditLedgerActivity(item, scope, english);
              return (
                <article key={`${scope}:${item.id}`}>
                  <span className={income ? 'is-income' : 'is-expense'}>{income ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}</span>
                  <div>
                    <div className="billing-evidence__entry-heading">
                      <strong>{getBillingLogLabel(item, english ? 'en-US' : 'zh-CN')}</strong>
                      <span className="billing-evidence__type" aria-label={`${english ? 'Activity type' : '流水类型'}：${activity.label}，${activity.scopeLabel}`}>
                        {english ? 'Type: ' : '类型：'}{activity.label}{activity.code.endsWith('_consumption') ? '' : ` · ${activity.scopeLabel}`}
                      </span>
                    </div>
                    <small>{formatBillingDate(item.created_at, { locale: english ? 'en-US' : 'zh-CN' })}{item.grouped ? ` · ${creditOperationStatus(item, english)}` : item.note ? ` · ${item.note}` : ''}</small>
                  </div>
                  <b className={income ? 'is-income' : 'is-expense'}>{amount > 0 ? '+' : ''}{amount.toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</b>
                  <CreditBalanceAfter value={item.balance_after} english={english} />
                </article>
              );
            })}
          </div>
        )}

        {!walletLoading && !walletError && walletPageCount > 1 && (
          <footer className="billing-evidence__footer">
            <div role="group" aria-label={english ? 'Credit history pages' : '点数流水分页'}><button type="button" disabled={walletPage <= 1} onClick={() => setWalletPage(value => Math.max(1, value - 1))}>{english ? 'Previous' : '上一页'}</button><span aria-live="polite">{walletPage} / {walletPageCount}</span><button type="button" disabled={walletPage >= walletPageCount} onClick={() => setWalletPage(value => Math.min(walletPageCount, value + 1))}>{english ? 'Next' : '下一页'}</button></div>
          </footer>
        )}
      </section>
    </div>
  );
}
